/**
 * What the Lazada screen and the automatic sync both need, in ONE place.
 *
 * ── WHY THE TOKEN RENEWAL IS NOT COPIED ──────────────────────────────────
 * The renewal is the one piece of this module that can destroy something:
 * Lazada ROTATES the refresh token, so a renewal whose write failed has already
 * killed the pair in the list. Two copies of that ordering is two chances to
 * get it wrong, so the automatic sync and the screen call the same function.
 */
import type { ILazadaStoredOrder } from "../../lib/sp/LazadaOrdersService";
import { toStoredOrder } from "../../lib/sp/LazadaOrdersService";
import { lazadaTokensService, tokenHealth, toStoredTokens } from "../../lib/sp/LazadaTokensService";
import { refreshLazadaTokens, type ILazadaApiOrder } from "./lazadaClient";

export const fromApi = (o: ILazadaApiOrder): ILazadaStoredOrder => toStoredOrder({
  id: o.id, number: o.number, createdAt: o.createdAt,
  status: o.status, statuses: o.statuses ?? [],
  total: o.total, currency: o.currency, items: o.items,
  city: o.city, province: o.province, paymentMethod: o.paymentMethod,
});

/**
 * A usable access token, renewed and re-stored if it is nearly out.
 *
 * Never returns a token it has not managed to store: failing here is
 * recoverable; carrying on is not. `onRenew` fires only when a renewal is
 * actually about to happen, so a screen can say so.
 */
export async function ensureLazadaAccessToken(
  me: string,
  onRenew?: () => void,
): Promise<string> {
  const read = await lazadaTokensService.read();
  if (!read.tokens) {
    throw new Error(
      read.missing
        ? "The Lazada token list does not exist yet — open Connection and create it."
        : "Not connected to Lazada. Open Connection and authorise the app.",
    );
  }
  const health = tokenHealth(read.tokens);
  if (health.needsReauthorise) {
    throw new Error(
      "The Lazada connection has lapsed — it must be authorised again on Lazada. "
      + "See Connection.",
    );
  }
  if (!health.needsRefresh) return read.tokens.accessToken;

  onRenew?.();
  const pair = await refreshLazadaTokens(read.tokens.refreshToken);
  const fresh = toStoredTokens(pair, me);
  await lazadaTokensService.save(fresh);
  return fresh.accessToken;
}
